import React, {useContext, useEffect, useState} from 'react';
import {
    Box,
    Checkbox,
    Header,
    Tabs} from "@amzn/awsui-components-react/polaris";
import { GlobalAppContext } from "src/components/App";
import OpenApprovalsGrid from "./OpenApprovalsGrid";
import OpenRequestsGrid from "./OpenRequestsGrid";
import { OpenApprovalsSearchRequest } from "src/models/dashboard/OpenApproval";
import { OpenRequestsSearchRequest } from "src/models/dashboard/OpenRequest";
import { getAllRequestsSearchPayload } from "src/components/dashboard/helpers/RequestsSearchHelper";
import { getAllApprovalsSearchPayload } from "src/components/dashboard/helpers/ApprovalsSearchHelper";
import { PropertyFilterQuery } from "@amzn/awsui-collection-hooks";
import { convertPropertyFilterQueryToDbMainExpression } from "./helpers/PropertyFilterHelper";

const OPEN_APPROVAL_TAB_ID = "allApprovalsTab";

export default function AllApprovalsAndRequestsView() {
    const { services } = useContext(GlobalAppContext);
    const [allApprovalsSearchPayload, setAllApprovalsSearchPayload] = useState(undefined as OpenApprovalsSearchRequest | undefined);
    const [allRequestsSearchPayload, setAllRequestsSearchPayload] = useState(undefined as OpenRequestsSearchRequest | undefined);
    const [activeTabId, setActiveTabId] = React.useState(OPEN_APPROVAL_TAB_ID);
    const [isAllUsersViewChecked, setIsAllUsersViewChecked] = React.useState(false);

    const [allApprovalsResult, isSearchingApprovals, searchApprovalsError] = services.dashboardService.searchApprovals(allApprovalsSearchPayload);
    const [allRequestsResult, isSearchingRequests, searchRequestsError] = services.dashboardService.searchRequests(allRequestsSearchPayload);

    document.body.classList.add('white-background');

    const getTotalCount = (tabId: string) => {
        const loading = tabId === OPEN_APPROVAL_TAB_ID? isSearchingApprovals : isSearchingRequests;
        const counter = tabId === OPEN_APPROVAL_TAB_ID? allApprovalsResult?.totalSize || 0 : allRequestsResult?.totalSize || 0;
        
        return loading? '...' : counter;
    }

    useEffect(() => {
        setAllApprovalsSearchPayload(getAllApprovalsSearchPayload());
        setAllRequestsSearchPayload(getAllRequestsSearchPayload());
    }, [])

    const setUserViewMode = (payload?: any) => {
        if (payload == null){
            return;
        }
        if (isAllUsersViewChecked) {
            if (activeTabId === OPEN_APPROVAL_TAB_ID){
                setAllApprovalsSearchPayload({
                    ...payload,
                    userViewMode: "ALL"
                })
            }
            else {
                setAllRequestsSearchPayload({
                    ...payload,
                    userViewMode: "ALL"
                })
            }
        }
        else {
            if (activeTabId === OPEN_APPROVAL_TAB_ID){
                setAllApprovalsSearchPayload({
                    ...payload,
                    userViewMode: "SINGLE"
                })
            }
            else {
                setAllRequestsSearchPayload({
                    ...payload,
                    userViewMode: "SINGLE"
                })
            }
        }
    }

    useEffect(() => {
        if (allApprovalsSearchPayload == null || allRequestsSearchPayload == null){
            return;
        }
        setUserViewMode(activeTabId === OPEN_APPROVAL_TAB_ID? allApprovalsSearchPayload : allRequestsSearchPayload);
    }, [isAllUsersViewChecked, activeTabId]);

    const approvalsFilterChangedHandler = (searchQuery:PropertyFilterQuery) => {
        if (allApprovalsSearchPayload == null){
            return;
        }
        if (searchQuery.tokens.length === 0){
            setAllApprovalsSearchPayload(getAllApprovalsSearchPayload());
            return;
        }
        setAllApprovalsSearchPayload({
            ...allApprovalsSearchPayload, 
            filterExpression: convertPropertyFilterQueryToDbMainExpression(searchQuery)
        })
    }
        
    const requestsFilterChangedHandler = (searchQuery:PropertyFilterQuery) => {
        if (allRequestsSearchPayload == null){
            return;
        }
        if (searchQuery.tokens.length === 0){
            setAllRequestsSearchPayload(getAllRequestsSearchPayload());
            return;
        }
        setAllRequestsSearchPayload({
            ...allRequestsSearchPayload,
            filterExpression: convertPropertyFilterQueryToDbMainExpression(searchQuery)})
    }

    return <div>
                <div className='allUsersViewContainer'>
                    <Checkbox
                        onChange={({ detail }) =>
                            setIsAllUsersViewChecked(detail.checked)
                        }
                        checked={isAllUsersViewChecked}
                        >
                        All users view
                    </Checkbox>
                </div>
                <Tabs data-class="allApprovalsAndRequestsTabs"
                    onChange={({ detail }) =>
                    setActiveTabId(detail.activeTabId)
                    }
                    activeTabId={activeTabId}
                    disableContentPaddings={true}
                    tabs={[
                        {
                        label: "All approvals",
                        id: "allApprovalsTab",
                        content: <OpenApprovalsGrid 
                                    openApprovalsResult={allApprovalsResult} 
                                    isLoading={isSearchingApprovals} 
                                    dashboardView={true}
                                    showFilter={true}
                                    gridHeader={<>
                                        <br/>
                                        <Header counter={`(${getTotalCount(activeTabId)})`} description="Assigned and relevant to me">All approvals</Header>
                                    </>} 
                                    onFilterTextChanged={approvalsFilterChangedHandler}
                                    onSortingChanged={(sortOrder:string, sortField?:string) => {
                                        if (allApprovalsSearchPayload == null){
                                            return;
                                        }
                                        setAllApprovalsSearchPayload({
                                            ...allApprovalsSearchPayload,
                                            sortField: sortField,
                                            sortOrder: sortOrder
                                        })
                                    }}/>
                        },
                        {
                        label: "All requests",
                        id: "allRequestsTab",
                        content: <OpenRequestsGrid 
                                    openRequestsResult={allRequestsResult} 
                                    isLoading={isSearchingRequests} 
                                    dashboardView={true}
                                    showFilter={true}
                                    gridHeader={<>
                                        <br/>
                                        <Header counter={`(${getTotalCount(activeTabId)})`} description="Assigned and relevant to me">All requests</Header>
                                    </>}
                                    onFilterTextChanged={requestsFilterChangedHandler}
                                    onSortingChanged={(sortOrder:string, sortField?:string) => {
                                        if (allRequestsSearchPayload == null){
                                            return;
                                        }
                                        setAllRequestsSearchPayload({
                                            ...allRequestsSearchPayload,
                                            sortField: sortField,
                                            sortOrder: sortOrder
                                        })
                                    }}/>
                        }
                    ]}
                />
            </div>
}